import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import { InvitesPageResponse, PendingInvite } from "../models/invite.models";

interface PendingInviteResponse {
    _id?: string;
    gmvid?: string;
    username?: string;
    email?: string;
    company?: string;
    clientId?: string;
    firstName?: string;
    familyName?: string;
    role?: string[];
    sendCallReport?: boolean;
    sendWorkflowReport?: boolean;
    twoFactor?: boolean;
}

interface UserCountResponse {
    preregistered?: number;
}

@Injectable({ providedIn: "root" })
export class InvitesApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly preregisterUrl = `${this.config.apiBaseUrl}/servauthen/preregister`;

    loadPage(token: string): Observable<InvitesPageResponse> {
        const params = token
            ? new HttpParams().set("earlierThan", token)
            : undefined;
        return this.http
            .get<
                PendingInviteResponse[]
            >(`${this.preregisterUrl}/find/bycompany`, { params })
            .pipe(
                map((response) => {
                    const invites = response.map((invite) =>
                        this.mapInvite(invite)
                    );
                    return {
                        invites,
                        nextToken: invites.length
                            ? invites.at(-1)?.gmvid || null
                            : null,
                    };
                })
            );
    }

    loadCount(): Observable<number> {
        return this.http
            .get<UserCountResponse>(
                `${this.config.apiBaseUrl}/servauthen/client/user/count_categorized`
            )
            .pipe(map((response) => Math.max(0, response.preregistered ?? 0)));
    }

    resend(invite: PendingInvite): Observable<unknown> {
        return this.http.post(this.preregisterUrl, [
            {
                id: invite.id,
                gmvid: invite.gmvid,
                objectId: invite.gmvid,
                username: invite.username,
                email: invite.email,
                company: invite.company,
                clientId: invite.clientId,
                firstName: invite.firstName,
                familyName: invite.familyName,
                role: invite.roles,
                sendCallReport: invite.sendCallReport,
                sendWorkflowReport: invite.sendWorkflowReport,
                twoFactor: invite.twoFactor,
            },
        ]);
    }

    revoke(gmvid: string): Observable<unknown> {
        return this.http.delete(
            `${this.preregisterUrl}/${encodeURIComponent(gmvid)}`
        );
    }

    private mapInvite(response: PendingInviteResponse): PendingInvite {
        const email = response.email?.trim() || response.username?.trim() || "";
        const gmvid = response.gmvid || response._id || email;
        return {
            id: gmvid,
            gmvid,
            username: response.username || email,
            email,
            company: response.company || "",
            clientId: response.clientId || "",
            firstName: response.firstName || "",
            familyName: response.familyName || "",
            roles: response.role ?? [],
            sendCallReport: Boolean(response.sendCallReport),
            sendWorkflowReport: Boolean(response.sendWorkflowReport),
            twoFactor: Boolean(response.twoFactor),
        };
    }
}
